import axiosInstance from "../common/axiosInstance";
import { UseMutationResult, UseQueryResult, useMutation, useQuery } from "@tanstack/react-query";
export const PATHS = Object.freeze({
  GET_CLIENT_STATS: "/calendar/client-stats",
  GET_USER_CALL_STATS: "/calendar/user-call-stats",
  GET_CALENDAR_CONNECTING_PEOPLE : "/calendar/connected-people",
  POST_CALENDAR_SYNCH: (calendarDomain: string) =>
    `/${calendarDomain}/sync`,
  POST_CALENDAR_CONNECT_DICONNECT: "/integrations/calendar-connection",
  GET_CALENDAR_DETAILS: "/integrations/calendar-details",
});

interface GetCalendarStatsParams {
  startTime: string;
  endTime: string;
  employeeEmail: string;
  impersonate_email?: string;
  isExternal: boolean;
}

interface GetConnectingWithPeopleParams {
  startTime: string;
  endTime: string;
  employeeEmail: string;
  impersonate_email?: string;
}

interface ConnectDisconnectCalendarParams {
  employeeEmail: string;
  isSyncEnabled: boolean;
}

interface GetCalendarDetailsParams {
  employeePorId: string;
}

const getCalendarStats = async (
  params: GetConnectingWithPeopleParams
): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_CLIENT_STATS, {
    params,
  });
  return response.data;
};

const useGetCalendarStats = (
    params: GetConnectingWithPeopleParams,
    enabled: boolean
  ) => {
    const stringifiedParams = JSON.stringify(params);
    return useQuery<any>({
      queryKey: ["get_calendar_stats", stringifiedParams],
      queryFn: () => getCalendarStats(params),
      enabled,
      retry: 0,
    });
  };

const getUserCallStats = async (
  params: GetCalendarStatsParams
): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_USER_CALL_STATS, {
    params,
  });
  return response.data;
};

const useGetUserCallStats = (
  params: GetCalendarStatsParams,
  enabled: boolean
) => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_user_call_stats", stringifiedParams],
    queryFn: () => getUserCallStats(params),
    enabled,
    retry: 0,
  });
};

const getCalendarConnectingPeople = async (params: GetConnectingWithPeopleParams): Promise<any> => {
    const response = await axiosInstance.get(PATHS.GET_CALENDAR_CONNECTING_PEOPLE, { params });
    return response.data;
  };

  const useGetCalendarConnectingPeople = (params: GetConnectingWithPeopleParams, enabled: boolean) => {
    const stringifiedParams = JSON.stringify(params);
    return useQuery<any>({
      queryKey: ["get_calendar_connecting_people", stringifiedParams],
      queryFn: () => getCalendarConnectingPeople(params),
      enabled,
      retry: 0,
    });
  };

  const syncCalendar = async (params: {
    email: string;
    calendarDomain: string;
    startTime: string;
    endTime: string;
  }): Promise<any> => {
    const url = PATHS.POST_CALENDAR_SYNCH(params.calendarDomain);
    const response = await axiosInstance.post(url, null, {
      params: {
        email: params.email,
        startTime: params.startTime,
        endTime: params.endTime,
      },
    });
    return response.data;
  };
  

  const useSyncCalendar = (
    params: {
      email: string;
      calendarDomain: string;
      startTime: string;
      endTime: string;
    },
    enabled: boolean
  ) => {
    const queryKey = [
      "sync_calendar",
      `${params.email}_${params.startTime}_${params.endTime}`,
    ];
  
    return useQuery<any>({
      queryKey,
      queryFn: () => syncCalendar(params),
      enabled,
      retry: 0,
    });
  };
  
  const connectDisconnectCalendar = async (
    params: ConnectDisconnectCalendarParams
  ): Promise<any> => {
    const response = await axiosInstance.put(
      PATHS.POST_CALENDAR_CONNECT_DICONNECT,
      null,
      { params }
    );
    return response.data;
  };
  
  const useConnectDisconnectCalendar = (
    options?: {
      onMutate?: () => void;
      onSuccess?: (data: any) => void;
      onError?: (error: any) => void;
      onSettled?: () => void;
    }
  ): UseMutationResult<any, unknown, ConnectDisconnectCalendarParams> => {
    return useMutation({
      mutationFn: connectDisconnectCalendar,
      ...options,
    });
  };

  const getCalendarDetails = async (
    params: GetCalendarDetailsParams
  ): Promise<any> => {
    const response = await axiosInstance.get(PATHS.GET_CALENDAR_DETAILS, {
      params,
    });
    return response.data;
  };

  const useGetCalendarDetails = (
    params: GetCalendarDetailsParams,
    enabled: boolean
  ): UseQueryResult<any, any> => {
    const stringifiedParams = JSON.stringify(params);
    return useQuery({
      queryKey: ["get_calendar_details", stringifiedParams],
      queryFn: () => getCalendarDetails(params),
      enabled,
      retry: 0,
    });
  };
  
export interface CalendarService {
  readonly useGetCalendarStats: (
    params: GetConnectingWithPeopleParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetUserCallStats: (
    params: GetCalendarStatsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetCalendarConnectingPeople: (
    params: GetConnectingWithPeopleParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useSyncCalendar: (
    params: {
      email: string;
      calendarDomain: string;
      startTime: string;
      endTime: string;
    },
    enabled: boolean
  ) => UseQueryResult<any, any>;
  
  readonly useConnectDisconnectCalendar: (
    options?: {
      onMutate?: () => void;
      onSuccess?: (data: any) => void;
      onError?: (error: any) => void;
      onSettled?: () => void;
    }
  ) => UseMutationResult<any, unknown, ConnectDisconnectCalendarParams>;
  
  readonly useGetCalendarDetails: (
    params: GetCalendarDetailsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;
  
}

const CalendarService: CalendarService = Object.freeze({
  useGetCalendarStats,
  useGetUserCallStats,
  useGetCalendarConnectingPeople,
  useSyncCalendar,
  useConnectDisconnectCalendar,
  useGetCalendarDetails,
});

export default CalendarService;
